/**
 * Crew settings, as one workspace.
 *
 * It used to be a dialog with four tabs that opened a second dialog for the
 * tag, which opened a third for the unlock list. Three modals deep is not a
 * settings screen; it is a maze with a Save button somewhere in it.
 *
 * This is the same shape as the app's own settings: nearly the whole window,
 * a list of sections down the left, the section in the middle, and - where a
 * section benefits from one - a live preview on the right. Moving between
 * sections keeps whatever you have not saved, because losing a half-written
 * description because you glanced at Members is the sort of thing that makes
 * people stop trying.
 *
 * ## What is and is not here
 *
 * Every control below maps to something the server actually does. Where the
 * brief asked for a section the backend has no support for - crew-wide chat
 * moderation defaults, per-crew voice policy, crew-wide notification
 * defaults - the section says so plainly rather than drawing a switch that
 * silently does nothing. A dead switch is worse than an honest absence: it
 * teaches people the app lies.
 *
 * ## Saving
 *
 * Two kinds of change live here and they behave differently on purpose:
 *
 *   - **Fields** (name, description, region…) save on their own, one PATCH
 *     per change, because that is what the old screen did and people expect
 *     a settings row to stick.
 *   - **The tag** is a draft. It has five interacting choices and a preview,
 *     and saving each keystroke would mean everybody in the crew sees six
 *     intermediate tags. Save and Discard sit in a bar that appears only
 *     when something is unsaved, and a failed save says so rather than
 *     pretending.
 *
 * Nothing about choosing a design touches the network: the whole preview is
 * local, because a design is a handful of CSS gradients. One request goes
 * out when the authorised person presses Save.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { api, serverUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { useLocal } from '@/stores/local';
import { Icon } from '@/icons/Icon';
import { CropPicture } from './CropPicture';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { absolute } from './user';
import { Button, Field, Input, Modal, Textarea, Toggle, useConfirm, useToast } from './ui';

import { Select } from './Select';
import { CrewTag, closeCrewHover, type CrewChip, type CrewDesign, type CrewTagFont } from './CrewTag';
import { closeUserCard } from './user';
import { CrewRoles } from './CrewRoles';
import { closeCrewPreview, type Crew } from './Crew';
import { CrewBanner, CrewMark } from './CrewArt';

/* ------------------------------------------------------------ the shape */

type Catalogue = {
  designs: CrewDesign[];
  inks: { id: string; name: string; colour: string | null }[];
  fonts: { id: string; name: string }[];
  textMax: number;
  milestones: number[];
};

type Draft = {
  tagText: string;
  tagDesign: string;
  tagInk: string;
  tagFont: CrewTagFont;
};

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: 'home' },
  { id: 'identity', label: 'Identity', icon: 'profile' },
  { id: 'tag', label: 'Crew tag', icon: 'sparkle' },
  { id: 'banner', label: 'Banner', icon: 'image' },
  { id: 'members', label: 'Members', icon: 'users' },
  { id: 'roles', label: 'Roles', icon: 'shield' },
  { id: 'progress', label: 'Progress', icon: 'chart' },
  { id: 'notifications', label: 'Notifications', icon: 'bell' },
  { id: 'danger', label: 'Ownership', icon: 'alert' }
] as const;
type Section = (typeof SECTIONS)[number]['id'];

/* --------------------------------------------------------- the workspace */

export function CrewWorkspace({ crew, onClose, onChanged }: {
  crew: Crew;
  onClose: () => void;
  onChanged: () => void;
}) {
  const catalog = useSession((s) => s.catalog);
  const toast = useToast();
  const [section, setSection] = useState<Section>('overview');
  const [find, setFind] = useState('');
  const [busy, setBusy] = useState(false);

  const tags: Catalogue = (catalog as unknown as { crewTags?: Catalogue })?.crewTags ?? FALLBACK;
  const a = crew.appearance;
  const owner = crew.myRole === 'owner';

  /* The tag draft. Kept for the life of the workspace, so wandering off to
     Members and back does not lose an unsaved design. */
  const [draft, setDraft] = useState<Draft>(() => ({
    tagText: a.tagText || crew.tag,
    tagDesign: a.tagDesign ?? 'co-op',
    tagInk: a.tagInk ?? 'frame',
    tagFont: (a.tagFont as CrewTagFont) ?? 'mono'
  }));

  const saved: Draft = {
    tagText: a.tagText || crew.tag,
    tagDesign: a.tagDesign ?? 'co-op',
    tagInk: a.tagInk ?? 'frame',
    tagFont: (a.tagFont as CrewTagFont) ?? 'mono'
  };
  const dirty = (Object.keys(saved) as (keyof Draft)[]).some((k) => draft[k] !== saved[k]);

  /* Whatever the draft currently describes, as the chip every preview uses.
     The same component the app uses, so a preview cannot flatter a design. */
  const preview: CrewChip = useMemo(() => {
    const base = tags.designs.find((d) => d.id === draft.tagDesign) ?? tags.designs[0]!;
    const ink = tags.inks.find((x) => x.id === draft.tagInk)?.colour;
    return {
      id: crew.id,
      tag: crew.tag,
      text: draft.tagText || crew.tag,
      name: crew.name,
      design: ink ? { ...base, ink } : base,
      font: draft.tagFont,
      motion: a.tagMotion !== false,
      icon: a.icon,
      banner: a.banner,
      description: crew.description,
      memberCount: crew.memberCount,
      level: crew.level.level
    };
  }, [draft, tags, crew, a]);

  async function patch(body: Record<string, unknown>, note?: string) {
    setBusy(true);
    try {
      await api.patch('/api/crews/' + crew.id, body);
      onChanged();
      if (note) toast(note, 'good');
      return true;
    } catch (err) {
      toast((err as Error).message, 'bad');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function saveTag() {
    const ok = await patch(draft as unknown as Record<string, unknown>);
    if (ok) toast('Your crew wears it now', 'good');
  }

  const shown = find.trim()
    ? SECTIONS.filter((s) => s.label.toLowerCase().includes(find.trim().toLowerCase()))
    : SECTIONS;

  /*
   * Escape, and a click on the dark outside, both close it - unless there is
   * unsaved work, which is the one case where closing by accident costs
   * something. The backdrop check is on mousedown's target being the
   * backdrop itself, so a drag that starts inside and ends outside does not
   * count as clicking away.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !dirty) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dirty, onClose]);

  const backdrop = useRef<HTMLDivElement>(null);
  const downOnBackdrop = useRef(false);

  /* Opening settings shuts the preview and the cards it was reached from:
     three layers of the same crew stacked on each other is how a screen gets
     left behind with no way to close it. */
  useEffect(() => {
    closeCrewPreview();
    closeUserCard();
    closeCrewHover();
  }, []);

  return (
    <div
      ref={backdrop}
      data-keeps-card
      onMouseDown={(e) => { downOnBackdrop.current = e.target === backdrop.current; }}
      onMouseUp={(e) => {
        if (!downOnBackdrop.current || e.target !== backdrop.current) return;
        downOnBackdrop.current = false;
        if (!dirty) onClose();
      }}
      className="fixed inset-0 z-[320] flex bg-[rgba(4,3,8,.72)] p-4 backdrop-blur-[3px] sm:p-8"
    >
      <div className="settings-in m-auto flex h-[min(900px,90vh)] w-[min(1380px,94vw)] overflow-hidden rounded-[var(--radius-xl)] border border-line bg-surface-0 shadow-[var(--shadow-3)]">

        {/* ---- the sections ---- */}
        <nav aria-label="Crew settings" className="hidden w-[248px] flex-none flex-col border-r border-line-soft bg-surface-1 p-4 sm:flex">
          <h2 className="m-0 mb-3 text-[21px] leading-tight font-extrabold tracking-[-.02em] text-ink">Crew settings</h2>
          <label className="mb-3 flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-2">
            <Icon name="search" size={14} className="flex-none text-ghost" />
            <input
              value={find}
              onChange={(e) => setFind(e.target.value)}
              placeholder="Search settings…"
              className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ghost"
            />
          </label>

          <ul className="m-0 flex min-h-0 flex-1 list-none flex-col gap-0.5 overflow-y-auto p-0">
            {shown.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSection(s.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] px-3 py-2.5 text-left text-[14px] font-semibold outline-none transition-colors',
                    section === s.id ? 'bg-accent-soft text-accent' : 'text-dim hover:bg-surface-2 hover:text-ink'
                  )}
                >
                  <Icon name={s.icon} size={16} className="flex-none" />
                  {s.label}
                  {s.id === 'tag' && dirty ? <span className="ml-auto h-1.5 w-1.5 rounded-full bg-warn" /> : null}
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={onClose}
            className="mt-3 flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] px-3 py-2.5 text-[14px] font-semibold text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink"
          >
            <Icon name="arrowL" size={16} />
            Back to crew
          </button>
        </nav>

        {/* ---- the section ---- */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex flex-none items-start gap-3 border-b border-line-soft px-5 py-4 sm:px-7">
            <div className="min-w-0 flex-1">
              <h3 className="m-0 text-[24px] leading-tight font-extrabold tracking-[-.02em] text-ink">
                {SECTIONS.find((s) => s.id === section)?.label}
              </h3>
              <p className="mt-1 mb-0 text-[13.5px] text-mute">{BLURBS[section](crew.name)}</p>
            </div>
            <button
              type="button"
              aria-label="Close crew settings"
              onClick={onClose}
              className="grid h-9 w-9 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] text-mute outline-none transition-colors hover:bg-surface-2 hover:text-ink"
            >
              <Icon name="close" size={16} />
            </button>
          </header>

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className="min-w-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">
              {section === 'overview' ? <Overview crew={crew} busy={busy} patch={patch} />
                : section === 'identity' ? <Identity crew={crew} busy={busy} patch={patch} />
                  : section === 'tag' ? (
                    <TagEditor
                      crew={crew} tags={tags} draft={draft} setDraft={setDraft}
                      preview={preview} onJumpToProgress={() => setSection('progress')}
                    />
                  )
                    : section === 'banner' ? <BannerBits crew={crew} busy={busy} patch={patch} />
                      : section === 'members' ? <Members crew={crew} onChanged={onChanged} />
                        : section === 'roles' ? <CrewRoles crew={crew} onChanged={onChanged} />
                          : section === 'progress' ? <Progress crew={crew} tags={tags} preview={preview} />
                            : section === 'notifications' ? <Notifications crew={crew} />
                              : <Danger crew={crew} owner={owner} onClose={onClose} onChanged={onChanged} />}
            </div>

            {/* The tag's preview rail. Every other section carries its own
                preview in its own column, because what is worth previewing is
                different in each - a banner needs two shapes, an identity
                needs a member list. */}
            {section === 'tag' ? (
              <aside className="hidden w-[380px] flex-none overflow-y-auto border-l border-line-soft bg-surface-1 px-5 py-5 xl:block">
                <LivePreview crew={preview} />
              </aside>
            ) : null}
          </div>

          {/* The save bar, only when there is something to save. */}
          {section === 'tag' && dirty ? (
            <footer className="flex flex-none items-center gap-3 border-t border-line-soft bg-surface-1 px-5 py-3.5 sm:px-7">
              <span className="flex items-center gap-2 text-[13px] font-semibold text-warn">
                <Icon name="alert" size={15} />
                Unsaved style changes
              </span>
              <span className="flex-1" />
              <Button kind="ghost" disabled={busy} onClick={() => setDraft(saved)}>Discard</Button>
              <Button kind="primary" disabled={busy} onClick={() => void saveTag()}>
                {busy ? 'Saving…' : 'Save crew style'}
              </Button>
            </footer>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const BLURBS: Record<Section, (name: string) => string> = {
  overview: (n) => 'What ' + n + ' is, and who can find it.',
  identity: (n) => 'The name and tag ' + n + ' is known by.',
  tag: (n) => 'Build a shared identity for ' + n + '.',
  banner: () => 'The picture and mark at the top of the crew.',
  members: () => 'Who is in, who is asking, and what they can do.',
  roles: () => 'What each role is allowed to do. These rules are the server’s.',
  progress: () => 'What the crew has earned together, and what is next.',
  notifications: () => 'Your own alerts for this crew.',
  danger: () => 'Handing it over, leaving, or closing it for good.'
};

/* ------------------------------------------------------------- sections */

/**
 * Crew overview: what the crew is, and how people find it.
 *
 * The two halves of a first impression, side by side with what a stranger
 * actually sees. Discovery is three cards rather than a dropdown because it
 * is the one setting here with consequences - "unlisted" and "private" are
 * different enough that a line of small print under a select is not enough.
 */
function Overview({ crew, busy, patch }: { crew: Crew; busy: boolean; patch: (b: Record<string, unknown>, n?: string) => Promise<boolean> }) {
  const catalog = useSession((s) => s.catalog);
  const can = crew.myRole === 'owner' || crew.myRole === 'officer';
  const [description, setDescription] = useState(crew.description);
  const [welcome, setWelcome] = useState(crew.welcome ?? '');
  const [adding, setAdding] = useState<'game' | 'style' | null>(null);

  const region = catalog?.regions.find((r) => r.id === crew.region)?.name ?? crew.region;
  const tags = crew.tags ?? [];

  /* What the crew plays, counted from its members rather than declared: a
     list somebody typed once is a list that is wrong by the second month. */
  const plays = (() => {
    const count = new Map<string, number>();
    for (const m of crew.members) for (const g of m.user.games ?? []) count.set(g.id, (count.get(g.id) ?? 0) + 1);
    return [...count.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([id]) => id);
  })();

  const visibility = crew.settings.visibility ?? 'public';

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-4">
        <Locked can={can} why="Only the owner and officers can change this." />

        <Card title="About your crew" note="Tell people what your crew is about.">
          <Textarea
            value={description}
            disabled={!can || busy}
            rows={3}
            maxLength={300}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => { if (description !== crew.description) void patch({ description }, 'Saved'); }}
          />
          <p className="m-0 mt-1 text-right text-[11.5px] text-ghost tabular-nums">{description.length}/300</p>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Language">
              <Select
                value={crew.language}
                disabled={!can}
                onChange={(v) => void patch({ language: v }, 'Saved')}
                options={(catalog?.languages ?? []).map((l) => ({ value: String(l), label: String(l) }))}
              />
            </Field>
            <Field label="Region">
              <Select
                value={crew.region}
                disabled={!can}
                onChange={(v) => void patch({ region: v }, 'Saved')}
                options={(catalog?.regions ?? []).map((r) => ({ value: r.id, label: r.name }))}
              />
            </Field>
          </div>
        </Card>

        <Card title="Discovery &amp; joining" note="Choose how people can find your crew and how they can join.">
          <div className="grid gap-2.5 sm:grid-cols-3">
            {([
              ['public', 'globe', 'Public', 'Anyone can find your crew and see details.'],
              ['unlisted', 'link', 'Unlisted', 'Only people with a link can find your crew.'],
              ['private', 'lock', 'Private', 'Only people you invite can find your crew.']
            ] as const).map(([id, icon, name, note]) => (
              <button
                key={id}
                type="button"
                disabled={!can || busy}
                onClick={() => void patch({ visibility: id }, 'Saved')}
                className={cn(
                  'flex flex-col gap-2 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3.5 text-left outline-none transition-[background-color,box-shadow]',
                  visibility === id ? 'shadow-[inset_0_0_0_2px_var(--accent)]' : 'shadow-[inset_0_0_0_1px_var(--line-soft)]',
                  can ? 'hover:bg-surface-3' : 'cursor-not-allowed opacity-60'
                )}
              >
                <span className="flex items-center gap-2">
                  <Icon name={icon} size={17} className={visibility === id ? 'text-accent' : 'text-mute'} />
                  <span className="flex-1 text-[14px] font-bold text-ink">{name}</span>
                  <span className={cn(
                    'grid h-[17px] w-[17px] place-items-center rounded-full',
                    visibility === id ? 'bg-accent text-accent-fg' : 'shadow-[inset_0_0_0_1.5px_var(--line)]'
                  )}>
                    {visibility === id ? <Icon name="check" size={10} stroke={3} /> : null}
                  </span>
                </span>
                <span className="text-[12px] leading-snug text-mute">{note}</span>
              </button>
            ))}
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Who can join">
              <Select
                value={crew.settings.join}
                disabled={!can}
                onChange={(v) => void patch({ join: v }, 'Saved')}
                options={[
                  { value: 'open', label: 'Anyone can join', sub: 'No approval needed.' },
                  { value: 'request', label: 'Request to join', sub: 'The owner and officers approve each one.' },
                  { value: 'invite', label: 'Invite only', sub: 'Nobody can ask; somebody has to invite them.' }
                ]}
              />
            </Field>
            <Field label="Who can see the members">
              <Select
                value={crew.settings.viewMembers}
                disabled={!can}
                onChange={(v) => void patch({ viewMembers: v }, 'Saved')}
                options={[{ value: 'everyone', label: 'Everyone' }, { value: 'members', label: 'Only the crew' }]}
              />
            </Field>
          </div>
        </Card>

        <Card title="Games &amp; playstyle" note="What your crew plays, and what it is like to play with.">
          <span className="mb-1.5 block text-[12px] text-mute">Usually playing</span>
          <div className="flex flex-wrap gap-2">
            {plays.map((id) => {
              const g = catalog?.games.find((x) => x.id === id);
              return (
                <span key={id} className="flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-2 text-[13px] font-semibold text-dim">
                  <GameMark id={id} size={17} plain />
                  {g?.name ?? id}
                </span>
              );
            })}
            {!plays.length ? (
              <span className="text-[13px] text-mute">Counted from what your members play. Nobody has listed a game yet.</span>
            ) : null}
          </div>

          <span className="mt-4 mb-1.5 block text-[12px] text-mute">Playstyle</span>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <span key={t} className="flex items-center gap-1.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-2 text-[13px] font-semibold text-dim">
                {t}
                {can ? (
                  <button
                    type="button"
                    aria-label={'Remove ' + t}
                    onClick={() => void patch({ tags: tags.filter((x) => x !== t) }, 'Saved')}
                    className="grid h-4 w-4 place-items-center rounded-full text-ghost outline-none hover:text-bad"
                  >
                    <Icon name="close" size={10} />
                  </button>
                ) : null}
              </span>
            ))}
            {can && tags.length < 4 ? (
              <button
                type="button"
                onClick={() => setAdding('style')}
                className="flex items-center gap-1.5 rounded-[calc(12px*var(--radius-mul,1))] px-2.5 py-2 text-[13px] font-semibold text-mute shadow-[inset_0_0_0_1px_var(--line)] outline-none transition-colors hover:text-ink"
              >
                <Icon name="plus" size={13} />
                Add playstyle
              </button>
            ) : null}
          </div>
        </Card>
      </div>

      {/* ---- what a stranger sees ---- */}
      <aside className="flex min-w-0 flex-col gap-4">
        <Card title="Discovery preview" note="This is how your crew appears to other Pugo users.">
          <div className="overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2">
            <CrewBanner appearance={crew.appearance} className="h-[120px] w-full" />
            <div className="p-3.5">
              <div className="-mt-10 mb-2.5 flex items-end gap-3">
                <span className="rounded-full bg-surface-2 p-1">
                  <CrewMark tag={crew.tag} appearance={crew.appearance} size={56} className="rounded-full" />
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[16px] font-bold text-ink uppercase">{crew.name}</span>
                <CrewTag crew={crew.chip} size="sm" still />
              </div>
              <p className="mt-1.5 mb-0 text-[13px] leading-snug text-dim">{description || 'No description yet.'}</p>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                <Chip icon="globe" label={crew.language} />
                <Chip icon="pin" label={region} />
                {tags[0] ? <Chip icon="gamepad" label={tags[0]} /> : null}
              </div>
              <div className="mt-3 flex h-[38px] items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent text-[13.5px] font-bold text-accent-fg">
                <Icon name="addFriend" size={15} />
                {crew.settings.join === 'open' ? 'Join crew' : crew.settings.join === 'request' ? 'Request to join' : 'Invite only'}
              </div>
            </div>
          </div>
        </Card>

        <Card title="Welcome new members" note="Set a friendly message for people who join your crew.">
          <Textarea
            value={welcome}
            disabled={!can || busy}
            rows={2}
            maxLength={200}
            placeholder="Welcome! Say hello and share what you play."
            onChange={(e) => setWelcome(e.target.value)}
            onBlur={() => { if (welcome !== (crew.welcome ?? '')) void patch({ welcome }, 'Saved'); }}
          />
          <p className="m-0 mt-1 text-right text-[11.5px] text-ghost tabular-nums">{welcome.length}/200</p>
          <div className="mt-2">
            <Toggle
              checked={crew.welcomeOn !== false}
              disabled={!can}
              onChange={(v: boolean) => void patch({ welcomeOn: v }, 'Saved')}
              label="Show welcome message"
              note="Shown to new members after they join."
            />
          </div>
        </Card>
      </aside>

      {adding === 'style' ? (
        <AddPlaystyle
          crew={crew}
          onClose={() => setAdding(null)}
          onPick={(v) => { void patch({ tags: [...tags, v] }, 'Saved'); setAdding(null); }}
        />
      ) : null}
    </div>
  );
}

function Chip({ icon, label }: { icon: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-surface-3 px-2.5 py-1 text-[12px] font-semibold text-dim">
      <Icon name={icon} size={12} className="text-mute" />
      {label}
    </span>
  );
}

function AddPlaystyle({ crew, onClose, onPick }: { crew: Crew; onClose: () => void; onPick: (v: string) => void }) {
  const catalog = useSession((s) => s.catalog);
  const have = new Set(crew.tags ?? []);
  const options = (catalog?.playstyles ?? []).map((p) => p.name).filter((n) => !have.has(n));
  return (
    <Modal title="Add a playstyle" subtitle="Up to four. They show on your crew's card." onClose={onClose}>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onPick(o)}
            className="rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2 text-[13.5px] font-semibold text-dim outline-none transition-colors hover:bg-surface-3 hover:text-ink"
          >
            {o}
          </button>
        ))}
        {!options.length ? <p className="m-0 text-[13.5px] text-mute">You have them all.</p> : null}
      </div>
    </Modal>
  );
}

/**
 * Crew identity: the name, the tag, the picture and the small brand choices.
 *
 * Deliberately separate from the Tag studio next door. This is "who are we
 * called"; that is "what does the plate look like". Putting them together
 * made one screen where half the controls were a month-long commitment and
 * the other half were a colour.
 */
function Identity({ crew, busy, patch }: { crew: Crew; busy: boolean; patch: (b: Record<string, unknown>, n?: string) => Promise<boolean> }) {
  const can = crew.myRole === 'owner' || crew.myRole === 'officer';
  const [name, setName] = useState(crew.name);
  const [tag, setTag] = useState(crew.tag);
  const [tagline, setTagline] = useState(crew.tagline ?? '');
  const retagAt = crew.canRetagAt;
  const icon = crew.appearance.icon ? absolute(crew.appearance.icon) : null;

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-4">
        <Locked can={can} why="Only the owner and officers can change this." />

        <Card title="Name &amp; tag" note="This is how your crew is known across Pugo.">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Field label="Crew name">
                <Input
                  value={name}
                  disabled={!can || busy}
                  maxLength={50}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => { if (name.trim() && name !== crew.name) void patch({ name: name.trim() }, 'Renamed'); }}
                />
              </Field>
              <p className="m-0 mt-1 text-right text-[11.5px] text-ghost tabular-nums">{name.length}/50</p>
            </div>
            <div>
              <Field label="Crew tag">
                <span className="relative block">
                  {retagAt ? (
                    <Icon name="lock" size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ghost" />
                  ) : null}
                  <Input
                    value={tag}
                    disabled={!can || busy || !!retagAt}
                    maxLength={5}
                    onChange={(e) => setTag(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    onBlur={() => { if (tag.length >= 3 && tag !== crew.tag) void patch({ tag }, 'Tag changed'); }}
                    className={cn('font-mono uppercase', retagAt && 'pl-9')}
                  />
                </span>
              </Field>
              <p className="m-0 mt-1 text-[11.5px] leading-snug text-ghost">
                {retagAt
                  ? 'Tag text can change again on ' + new Date(retagAt).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) + '.'
                  : '3 to 5 characters. Changing it starts a month’s cooldown.'}
              </p>
            </div>
          </div>
        </Card>

        <Card title="Crew picture" note="This is your crew's avatar. It appears in lists, chat and invitations.">
          <div className="flex flex-wrap items-center gap-4">
            <span className="h-[104px] w-[104px] flex-none overflow-hidden rounded-full bg-surface-2 shadow-[inset_0_0_0_2px_var(--line-soft)]">
              {icon
                ? <img src={icon} alt="" className="h-full w-full object-cover" />
                : <CrewMark tag={crew.tag} appearance={crew.appearance} size={104} className="rounded-full" />}
            </span>
            <span className="h-[80px] w-[80px] flex-none overflow-hidden rounded-[calc(18px*var(--radius-mul,1))] bg-surface-2">
              {icon
                ? <img src={icon} alt="" className="h-full w-full object-cover" />
                : <CrewMark tag={crew.tag} appearance={crew.appearance} size={80} />}
            </span>
            <span className="min-w-[180px] flex-1 text-[12.5px] leading-relaxed text-mute">
              Shown round in most places and square in a few. A square picture with the subject
              in the middle survives both.
              <span className="mt-2 block text-[11.5px] text-ghost">Change it under Banner &amp; picture.</span>
            </span>
          </div>
        </Card>

        <Card title="Brand details" note="Fine-tune how your crew looks. These are aesthetic choices and do not affect roles or permissions.">
          <Field label="Tagline" hint="One line, under the name.">
            <Input
              value={tagline}
              disabled={!can || busy}
              maxLength={120}
              placeholder="Good games. Better company."
              onChange={(e) => setTagline(e.target.value)}
              onBlur={() => { if (tagline !== (crew.tagline ?? '')) void patch({ tagline }, 'Saved'); }}
            />
          </Field>
          <p className="m-0 mt-1 text-right text-[11.5px] text-ghost tabular-nums">{tagline.length}/120</p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              kind="ghost"
              icon={<Icon name="refresh" size={14} />}
              disabled={!can || busy}
              onClick={() => void patch(
                { color: null, background: 'none', tagDesign: 'co-op', tagInk: 'frame', tagFont: 'mono', tagText: null },
                'Back to the default look'
              )}
            >
              Restore default appearance
            </Button>
            <span className="text-[11.5px] text-ghost">Pictures and the banner are left alone.</span>
          </div>
        </Card>
      </div>

      {/* ---- how it reads elsewhere ---- */}
      <aside className="flex min-w-0 flex-col gap-4">
        <Card title="Identity preview" note="See how your crew appears across Pugo.">
          <div className="overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2">
            <CrewBanner appearance={crew.appearance} className="h-[110px] w-full" />
            <div className="p-3.5">
              <div className="-mt-10 mb-2 flex items-end">
                <span className="rounded-full bg-surface-2 p-1">
                  <CrewMark tag={crew.tag} appearance={crew.appearance} size={52} className="rounded-full" />
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[15px] font-bold text-ink uppercase">{name}</span>
                <CrewTag crew={crew.chip} size="sm" still />
              </div>
              {tagline ? <p className="m-0 mt-1 text-[12.5px] text-mute">{tagline}</p> : null}
            </div>
          </div>

          <div className="mt-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3">
            <span className="mb-2 block text-[11px] font-semibold tracking-[.07em] text-ghost uppercase">Member list</span>
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {crew.members.slice(0, 3).map((m) => (
                <li key={m.user.id} className="flex items-center gap-2.5">
                  <Avatar user={m.user} size={24} />
                  <span className="min-w-0 flex-1 truncate text-[12.5px] font-bold text-ink">{m.user.displayName}</span>
                  <CrewTag crew={crew.chip} size="xs" still />
                  <span className="w-[52px] flex-none text-right text-[11.5px] text-mute">
                    {(crew.roles ?? []).find((r) => r.id === m.role)?.name ?? (m.role === 'owner' ? 'Owner' : m.role)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        <Card title="What changes where" note="A quick guide to what each part of your identity affects.">
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {([
              ['edit', 'Name', 'Shown on crew pages and in search results.'],
              ['sparkle', 'Tag', 'Shown in chat and on user profiles.'],
              ['image', 'Picture', 'Shown in crew lists and invitations.']
            ] as const).map(([icon, what, where]) => (
              <li key={what} className="flex items-start gap-3">
                <Icon name={icon} size={17} className="mt-0.5 flex-none text-ghost" />
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-bold text-ink">{what}</span>
                  <span className="block text-[12.5px] leading-snug text-mute">{where}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </aside>
    </div>
  );
}

/* --------------------------------------------------------- the tag studio */

/**
 * Choosing the crew's tag.
 *
 * Twelve frames, drawn for Pugo, and the four things a crew decides about
 * them: which frame, what it reads, what colour the letters are, and the
 * letterform. Everything else that used to be here - shape, finish, accent -
 * belonged to the CSS version and would now mean editing somebody's artwork.
 *
 * Nothing touches the network while you look around. The preview is the real
 * component at the real sizes, and one request goes out on Save.
 */
function TagEditor({ crew, tags, draft, setDraft, preview, onJumpToProgress }: {
  crew: Crew;
  tags: Catalogue;
  draft: Draft;
  setDraft: (fn: (d: Draft) => Draft) => void;
  preview: CrewChip;
  onJumpToProgress: () => void;
}) {
  const me = useSession((s) => s.me);
  const cosmetics = useLocal((s) => s.crewCosmetics);
  const setCosmetics = useLocal((s) => s.setCrewCosmetics);
  const motion = useLocal((s) => s.crewTagMotion);
  const setMotion = useLocal((s) => s.setCrewTagMotion);
  const can = crew.myRole === 'owner' || crew.myRole === 'officer';
  const [find, setFind] = useState('');
  const [only, setOnly] = useState<'all' | 'unlocked'>('all');

  /* Pugo's own staff see all twelve - they have to be able to check what they
     are moderating. The server enforces the same rule. */
  /* Administrators only. The server checks the same thing. */
  const staff = me?.role === 'admin';
  const unlocked = new Set<string>(
    staff ? tags.designs.map((d) => d.id) : (crew.unlockedTags ?? tags.designs.filter((d) => d.level <= 1).map((d) => d.id))
  );

  const shown = tags.designs.filter((d) => {
    if (only === 'unlocked' && !unlocked.has(d.id)) return false;
    const q = find.trim().toLowerCase();
    return !q || d.name.toLowerCase().includes(q) || (d.note ?? '').toLowerCase().includes(q);
  });

  return (
    <div className="flex flex-col gap-5">
      <Locked can={can} why="Only the owner and officers can change the crew style. You can still look." />

      {/* What the plate reads. Separate from the crew's tag, which is an
          identifier other crews cannot share. */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-[200px]">
          <Field label="Tag text" hint={'Up to ' + tags.textMax + ' letters or numbers.'}>
            <Input
              value={draft.tagText}
              disabled={!can}
              maxLength={tags.textMax}
              onChange={(e) => setDraft((d) => ({ ...d, tagText: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
              className="font-mono uppercase"
            />
          </Field>
        </div>

        <label className="flex min-w-[200px] flex-1 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5">
          <Icon name="search" size={15} className="flex-none text-ghost" />
          <input
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder="Search designs…"
            className="min-w-0 flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ghost"
          />
        </label>

        <div className="flex flex-none rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-1">
          {(['all', 'unlocked'] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setOnly(k)}
              className={cn(
                'h-8 rounded-[calc(8px*var(--radius-mul,1))] px-3 text-[12.5px] font-bold capitalize outline-none transition-colors',
                only === k ? 'bg-accent text-accent-fg' : 'text-dim hover:text-ink'
              )}
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <Group title="Designs" note={staff ? 'All twelve, because you are staff.' : 'Unlocked by your crew’s level.'}>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 2xl:grid-cols-4">
          {shown.map((d) => {
            const open = unlocked.has(d.id);
            const on = draft.tagDesign === d.id;
            return (
              <button
                key={d.id}
                type="button"
                disabled={!open || !can}
                onClick={() => setDraft((x) => ({ ...x, tagDesign: d.id }))}
                className={cn(
                  'relative flex flex-col items-center gap-2 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3 text-center outline-none transition-[background-color,box-shadow]',
                  on ? 'shadow-[inset_0_0_0_2px_var(--accent)]' : 'shadow-[inset_0_0_0_1px_var(--line-soft)]',
                  open && can ? 'hover:bg-surface-3' : 'cursor-not-allowed opacity-60'
                )}
              >
                {!open ? (
                  <span className="absolute top-2 right-2 flex items-center gap-1 text-[10.5px] font-semibold text-ghost">
                    <Icon name="lock" size={10} />
                    {d.level}
                  </span>
                ) : on ? (
                  <span className="absolute top-2 right-2 grid h-[18px] w-[18px] place-items-center rounded-full bg-accent text-accent-fg">
                    <Icon name="check" size={11} stroke={3} />
                  </span>
                ) : null}
                <CrewTag crew={{ ...preview, design: d }} size="lg" still />
                <span className="text-[13px] font-bold text-ink">{d.name}</span>
                <span className="text-[11.5px] leading-snug text-mute">{d.note}</span>
              </button>
            );
          })}
          {!shown.length ? (
            <p className="col-span-full m-0 py-6 text-center text-[13.5px] text-mute">Nothing matches that.</p>
          ) : null}
        </div>

        {unlocked.size < tags.designs.length ? (
          <button
            type="button"
            onClick={onJumpToProgress}
            className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold text-accent outline-none hover:underline"
          >
            <Icon name="chart" size={14} />
            {tags.designs.length - unlocked.size} still to unlock
          </button>
        ) : null}
      </Group>

      <div className="grid gap-4 lg:grid-cols-2">
        <Group title="Letter colour" note="Usually the frame's own. The rest are there for a name that reads badly in it.">
          <div className="grid grid-cols-4 gap-2">
            {tags.inks.map((ink) => (
              <Pick
                key={ink.id}
                on={draft.tagInk === ink.id}
                disabled={!can}
                label={ink.name}
                onClick={() => setDraft((d) => ({ ...d, tagInk: ink.id }))}
              >
                <span
                  className="h-5 w-5 rounded-full shadow-[inset_0_0_0_1px_rgba(255,255,255,.2)]"
                  style={{ background: ink.colour ?? 'linear-gradient(135deg,#e6f3e2,#f2cf7a)' }}
                />
              </Pick>
            ))}
          </div>
        </Group>

        <Group title="Letterform" note="All four fit the plate.">
          <div className="grid grid-cols-4 gap-2">
            {tags.fonts.map((fo) => (
              <Pick
                key={fo.id}
                on={draft.tagFont === fo.id}
                disabled={!can}
                label={fo.name}
                onClick={() => setDraft((d) => ({ ...d, tagFont: fo.id as CrewTagFont }))}
              >
                <CrewTag crew={{ ...preview, font: fo.id as CrewTagFont }} size="md" still />
              </Pick>
            ))}
          </div>
        </Group>
      </div>

      {/* The viewer's own switches. Labelled as theirs, because everything
          above changes what the whole crew sees and these two do not. */}
      <Group title="Only for you" note="These settings are just for you, on this device.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Toggle
            checked={cosmetics}
            onChange={setCosmetics}
            label="Show crew cosmetics"
            note="On by default. Off keeps a plain tag and fetches none of the artwork."
          />
          <Toggle
            checked={motion}
            onChange={setMotion}
            label="Subtle hover motion"
            note="A small lift when you point at a tag. Reduced motion is respected either way."
          />
        </div>
      </Group>
    </div>
  );
}

/** One preview column: the four places a tag actually appears. */
function LivePreview({ crew }: { crew: CrewChip }) {
  const me = useSession((s) => s.me);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="m-0 text-[15px] font-bold text-ink">Live preview</h4>
        <p className="mt-1 mb-0 text-[12.5px] text-mute">At the size it is really drawn, not a showroom.</p>
      </div>

      <Panel label="Chat">
        <div className="flex items-start gap-2.5">
          {me ? <Avatar user={me} size={32} /> : <span className="h-8 w-8 rounded-[30%] bg-surface-3" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[13.5px] font-bold text-ink">{me?.displayName ?? 'You'}</span>
              <CrewTag crew={crew} size="sm" still />
              <span className="text-[11px] text-ghost">Today at 7:24 PM</span>
            </div>
            <p className="m-0 mt-0.5 text-[13.5px] text-dim">Ready for game night?</p>
          </div>
        </div>
      </Panel>

      <Panel label="Quick profile">
        <div className="flex items-center gap-3">
          {me ? <Avatar user={me} size={44} presence /> : <span className="h-11 w-11 rounded-[30%] bg-surface-3" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-bold text-ink">{me?.displayName ?? 'You'}</span>
              <CrewTag crew={crew} size="sm" still />
            </div>
            <span className="flex items-center gap-1.5 text-[12.5px] text-mute">
              <i className="h-2 w-2 rounded-full bg-good" />
              Online
            </span>
          </div>
        </div>
      </Panel>

      <Panel label="Member row">
        <div className="flex items-center gap-2.5">
          {me ? <Avatar user={me} size={28} /> : <span className="h-7 w-7 rounded-[30%] bg-surface-3" />}
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">{me?.displayName ?? 'You'}</span>
          <CrewTag crew={crew} size="xs" still />
          <span className="text-[11.5px] text-ghost">Officer</span>
        </div>
      </Panel>

      <Panel label="Voice bar">
        <div className="flex items-center gap-2.5 rounded-full bg-surface-3 px-2.5 py-2">
          {me ? <Avatar user={me} size={26} /> : <span className="h-[26px] w-[26px] rounded-full bg-surface-4" />}
          <span className="text-[13px] font-bold text-ink">{me?.displayName ?? 'You'}</span>
          <CrewTag crew={crew} size="sm" still />
          <span className="flex-1" />
          <Icon name="mic" size={14} className="text-good" />
        </div>
      </Panel>

      <p className="m-0 text-[11.5px] leading-relaxed text-ghost">
        Style applies to your crew. Display switches apply only to you.
      </p>
    </div>
  );
}

function Panel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3">
      <h5 className="m-0 mb-2 text-[11px] font-semibold tracking-[.07em] text-ghost uppercase">{label}</h5>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------- banner */

/**
 * The banner and the crew picture, framed.
 *
 * The old section was two upload buttons and a sentence apologising for the
 * lack of a crop tool. This is the crop tool: drag to move the picture
 * inside the frame, zoom, and choose whether it fills the frame or fits
 * inside it. The numbers are saved with the crew, so everybody sees the same
 * framing rather than whatever their screen happened to do to it.
 *
 * Two previews, because the frame is a different shape in each: wide on a
 * desktop, nearly square on a phone. A banner that works on one and cuts
 * somebody's head off on the other is the whole reason this exists.
 */
function BannerBits({ crew, busy, patch }: { crew: Crew; busy: boolean; patch: (b: Record<string, unknown>, n?: string) => Promise<boolean> }) {
  const toast = useToast();
  const can = crew.myRole === 'owner' || crew.myRole === 'officer';
  const [uploading, setUploading] = useState<'banner' | 'icon' | null>(null);
  const [shape, setShape] = useState<'desktop' | 'mobile'>('desktop');
  const bannerInput = useRef<HTMLInputElement>(null);
  const iconInput = useRef<HTMLInputElement>(null);

  /* The framing, as a draft: dragging should not be forty requests. */
  const saved = crew.appearance.bannerCrop ?? { x: 50, y: 50, zoom: 1, fit: 'fill' as const };
  const [crop, setCrop] = useState(saved);
  const dirty = crop.x !== saved.x || crop.y !== saved.y || crop.zoom !== saved.zoom || crop.fit !== saved.fit;

  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const banner = crew.appearance.banner ? absolute(crew.appearance.banner) : null;
  const icon = crew.appearance.icon ? absolute(crew.appearance.icon) : null;

  function onDown(e: React.PointerEvent) {
    if (!can || !banner) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: crop.x, oy: crop.y };
  }
  function onMove(e: React.PointerEvent) {
    const d = drag.current;
    const box = frame.current?.getBoundingClientRect();
    if (!d || !box) return;
    /* Moving the picture a frame's width moves the focal point the whole
       way across, so the drag feels like moving the picture rather than
       nudging a number. */
    setCrop((c) => ({
      ...c,
      x: Math.max(0, Math.min(100, d.ox - ((e.clientX - d.x) / box.width) * 100)),
      y: Math.max(0, Math.min(100, d.oy - ((e.clientY - d.y) / box.height) * 100))
    }));
  }
  function onUp() { drag.current = null; }

  /*
   * Framed before it is sent, the same as a profile picture.
   *
   * A crew picture was uploaded whole and then positioned afterwards with
   * the three sliders below - which works, but only the person who set it
   * ever sees the controls, and everybody else sees whatever the middle of
   * the picture happened to be. Choosing the crop up front means what you
   * approve is what the crew gets.
   */
  const [cropping, setCropping] = useState<{ file: File; kind: 'banner' | 'icon' } | null>(null);

  async function send(kind: 'banner' | 'icon', blob: Blob, name: string) {
    setUploading(kind);
    try {
      const res = await fetch(serverUrl() + '/api/files', {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + (localStorage.getItem('pogu.token') ?? ''),
          'Content-Type': blob.type || 'image/webp',
          'X-File-Type': blob.type || 'image/webp',
          'X-File-Name': encodeURIComponent(name),
          'X-File-Kind': 'banner'
        },
        body: blob
      });
      if (!res.ok) throw new Error('That picture would not upload.');
      const out = await res.json();
      await patch({ [kind]: out.file?.id ?? out.id }, kind === 'banner' ? 'Banner set' : 'Crew picture set');
      /* The crop is baked in now, so the position controls go back to the
         middle rather than re-cropping a picture that is already right. */
      if (kind === 'banner') setCrop({ x: 50, y: 50, zoom: 1, fit: 'fill' });
      setCropping(null);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setUploading(null);
    }
  }

  const style = banner
    ? {
        backgroundImage: 'url(' + banner + ')',
        backgroundSize: crop.fit === 'fill' ? (crop.zoom * 100) + '% auto' : 'contain',
        backgroundPosition: crop.x + '% ' + crop.y + '%',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'var(--bg-2)'
      }
    : undefined;

  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-4">
        <Locked can={can} why="Only the owner and officers can change the crew's pictures." />

        {cropping ? (
          <CropPicture
            file={cropping.file}
            shapes={cropping.kind === 'icon' ? ['avatar'] : ['banner']}
            busy={!!uploading}
            onCancel={() => setCropping(null)}
            onDone={(out) => void send(cropping.kind, out[0]!.blob, cropping.kind + '.webp')}
          />
        ) : null}

        <input ref={iconInput} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropping({ file: f, kind: 'icon' }); e.target.value = ''; }} />
        <input ref={bannerInput} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) setCropping({ file: f, kind: 'banner' }); e.target.value = ''; }} />

        <Card
          title={
            <span className="flex items-center gap-3">
              <span className="flex-1">Crew banner</span>
              <span className="flex rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 p-1 text-[12.5px] font-bold">
                {(['desktop', 'mobile'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setShape(k)}
                    className={cn(
                      'flex h-7 items-center gap-1.5 rounded-[calc(8px*var(--radius-mul,1))] px-2.5 capitalize outline-none transition-colors',
                      shape === k ? 'bg-accent text-accent-fg' : 'text-dim hover:text-ink'
                    )}
                  >
                    <Icon name={k === 'desktop' ? 'monitor' : 'laptop'} size={13} />
                    {k}
                  </button>
                ))}
              </span>
            </span>
          }
          note="Drag to reposition. Keep your crew's key content inside the safe area."
        >
          <div
            ref={frame}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            className={cn(
              'relative overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2',
              shape === 'desktop' ? 'aspect-[3/1]' : 'aspect-[5/3]',
              can && banner ? 'cursor-grab active:cursor-grabbing' : ''
            )}
            style={style}
          >
            {!banner ? (
              <span className="absolute inset-0 grid place-items-center text-[13px] text-mute">No banner yet.</span>
            ) : (
              <>
                {/* The safe area: what survives on every screen. */}
                <span className="pointer-events-none absolute inset-[8%_6%] rounded-[6px] border border-dashed border-white/45" />
                <span className="pointer-events-none absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/70" />
                <span className="pointer-events-none absolute right-2 bottom-2 rounded-[6px] bg-black/50 px-2 py-1 text-[11px] text-white/80">
                  Safe area (keeps key content visible)
                </span>
              </>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2">
              <span className="text-[12.5px] text-mute">Zoom</span>
              <button
                type="button"
                aria-label="Zoom out"
                disabled={!can || !banner}
                onClick={() => setCrop((c) => ({ ...c, zoom: Math.max(1, Math.round((c.zoom - 0.1) * 10) / 10) }))}
                className="grid h-8 w-8 place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 text-dim outline-none hover:bg-surface-3 disabled:opacity-40"
              >
                <Icon name="minus" size={14} />
              </button>
              <input
                type="range"
                min={100}
                max={250}
                step={5}
                disabled={!can || !banner}
                value={Math.round(crop.zoom * 100)}
                onChange={(e) => setCrop((c) => ({ ...c, zoom: Number(e.target.value) / 100 }))}
                className="w-[150px] accent-[var(--accent)]"
              />
              <button
                type="button"
                aria-label="Zoom in"
                disabled={!can || !banner}
                onClick={() => setCrop((c) => ({ ...c, zoom: Math.min(2.5, Math.round((c.zoom + 0.1) * 10) / 10) }))}
                className="grid h-8 w-8 place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 text-dim outline-none hover:bg-surface-3 disabled:opacity-40"
              >
                <Icon name="plus" size={14} />
              </button>
            </span>

            <span className="flex rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 p-1 text-[12.5px] font-bold">
              {(['fit', 'fill'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  disabled={!can || !banner}
                  onClick={() => setCrop((c) => ({ ...c, fit: k }))}
                  className={cn(
                    'h-7 rounded-[calc(8px*var(--radius-mul,1))] px-3 capitalize outline-none transition-colors',
                    crop.fit === k ? 'bg-accent text-accent-fg' : 'text-dim hover:text-ink'
                  )}
                >
                  {k}
                </button>
              ))}
            </span>

            <Button
              size="sm"
              kind="ghost"
              icon={<Icon name="refresh" size={14} />}
              disabled={!can || !banner}
              onClick={() => setCrop({ x: 50, y: 50, zoom: 1, fit: 'fill' })}
            >
              Reset crop
            </Button>

            <span className="flex-1" />
            {dirty ? (
              <Button size="sm" kind="primary" disabled={busy} onClick={() => void patch({ bannerCrop: crop }, 'Framing saved')}>
                Save framing
              </Button>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" kind="outline" icon={<Icon name="upload" size={14} />} disabled={!can || !!uploading} onClick={() => bannerInput.current?.click()}>
              {uploading === 'banner' ? 'Uploading…' : banner ? 'Replace banner' : 'Choose a banner'}
            </Button>
            {banner ? (
              <Button size="sm" kind="ghost" disabled={!can || busy} onClick={() => void patch({ banner: null }, 'Banner removed')}>
                Remove banner
              </Button>
            ) : null}
          </div>
        </Card>

        <Card title="Crew picture" note="This picture appears with your crew across Pugo.">
          <div className="flex flex-wrap items-center gap-3">
            <span className="h-[62px] w-[62px] flex-none overflow-hidden rounded-full bg-surface-2">
              {icon ? <img src={icon} alt="" className="h-full w-full object-cover" /> : <CrewMark tag={crew.tag} appearance={crew.appearance} size={62} className="rounded-full" />}
            </span>
            <span className="h-[62px] w-[62px] flex-none overflow-hidden rounded-[calc(16px*var(--radius-mul,1))] bg-surface-2">
              {icon ? <img src={icon} alt="" className="h-full w-full object-cover" /> : <CrewMark tag={crew.tag} appearance={crew.appearance} size={62} />}
            </span>
            <Button size="sm" kind="outline" icon={<Icon name="image" size={14} />} disabled={!can || !!uploading} onClick={() => iconInput.current?.click()}>
              {uploading === 'icon' ? 'Uploading…' : 'Change picture'}
            </Button>
            {icon ? (
              <Button size="sm" kind="ghost" disabled={!can || busy} onClick={() => void patch({ icon: null }, 'Removed')}>Remove</Button>
            ) : null}
          </div>
        </Card>
      </div>

      {/* ---- the two shapes it has to survive ---- */}
      <aside className="flex min-w-0 flex-col gap-4">
        <Card title="Live preview" note="See how your crew looks in different places.">
          <span className="mb-1.5 block text-[11.5px] font-semibold tracking-[.06em] text-ghost uppercase">Desktop</span>
          <div className="overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2">
            <div className="aspect-[3/1] w-full" style={style} />
            <div className="flex items-center gap-2.5 p-2.5">
              <span className="h-9 w-9 flex-none overflow-hidden rounded-full bg-surface-3">
                {icon ? <img src={icon} alt="" className="h-full w-full object-cover" /> : <CrewMark tag={crew.tag} appearance={crew.appearance} size={36} className="rounded-full" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-ink uppercase">{crew.name}</span>
              <CrewTag crew={crew.chip} size="xs" still />
            </div>
          </div>

          <span className="mt-3 mb-1.5 block text-[11.5px] font-semibold tracking-[.06em] text-ghost uppercase">Mobile</span>
          <div className="mx-auto w-[190px] overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2">
            <div className="aspect-[5/3] w-full" style={style} />
            <div className="flex items-center gap-2 p-2">
              <span className="h-7 w-7 flex-none overflow-hidden rounded-full bg-surface-3">
                {icon ? <img src={icon} alt="" className="h-full w-full object-cover" /> : <CrewMark tag={crew.tag} appearance={crew.appearance} size={28} className="rounded-full" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-[12px] font-bold text-ink uppercase">{crew.name}</span>
            </div>
          </div>
        </Card>

        <Card title="Image details" note="What works best in these frames.">
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {([
              ['image', 'PNG or JPEG', 'Resized on your machine before it is sent.'],
              ['expand', '3:1 on desktop', 'And about 5:3 on a phone - hence two previews.'],
              ['info', 'Keep faces central', 'The edges are the first thing a narrower screen loses.']
            ] as const).map(([icon, what, note]) => (
              <li key={what} className="flex items-start gap-2.5">
                <Icon name={icon} size={16} className="mt-0.5 flex-none text-ghost" />
                <span className="min-w-0">
                  <span className="block text-[13px] font-bold text-ink">{what}</span>
                  <span className="block text-[12px] leading-snug text-mute">{note}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </aside>
    </div>
  );
}

/* ------------------------------------------------------------ members */

function Members({ crew, onChanged }: { crew: Crew; onChanged: () => void }) {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [find, setFind] = useState('');
  const [busy, setBusy] = useState(false);
  const me = useSession((s) => s.me);
  const can = crew.myRole === 'owner' || crew.myRole === 'officer';

  async function act(path: string, body: unknown, note: string) {
    setBusy(true);
    try {
      await api.post(path, body);
      onChanged();
      toast(note, 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  /* The crew's own roles, for the picker and for the colour beside a name. */
  const roles = crew.roles ?? [];
  const roleOf = (id: string) => roles.find((r) => r.id === id);
  const counts = new Map<string, number>();
  for (const m of crew.members) counts.set(m.role, (counts.get(m.role) ?? 0) + 1);

  const list = crew.members.filter((m) =>
    !find.trim() || m.user.displayName.toLowerCase().includes(find.trim().toLowerCase())
    || m.user.username.toLowerCase().includes(find.trim().toLowerCase()));

  return (
    <div className="flex max-w-[820px] flex-col gap-5">
      {dialog}

      {crew.requests.length ? (
        <Group title={'Asking to join (' + crew.requests.length + ')'} note="They are waiting on you.">
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {crew.requests.map((r) => (
              <li key={r.user.id} className="flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3">
                <Avatar user={r.user} size={34} />
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-ink">{r.user.displayName}</span>
                <Button size="sm" kind="ghost" disabled={busy} onClick={() => void act('/api/crews/' + crew.id + '/requests/' + r.user.id, { accept: false }, 'Turned down')}>Decline</Button>
                <Button size="sm" kind="primary" disabled={busy} onClick={() => void act('/api/crews/' + crew.id + '/requests/' + r.user.id, { accept: true }, 'Welcome aboard')}>Accept</Button>
              </li>
            ))}
          </ul>
        </Group>
      ) : null}

      <label className="flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5">
        <Icon name="search" size={15} className="flex-none text-ghost" />
        <input
          value={find}
          onChange={(e) => setFind(e.target.value)}
          placeholder={'Search ' + crew.memberCount + ' members…'}
          className="min-w-0 flex-1 bg-transparent text-[14px] text-ink outline-none placeholder:text-ghost"
        />
      </label>

      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {list.map((m) => {
          const isMe = m.user.id === me?.id;
          /* Below me in rank, and never the owner. Worked out from the
             crew's own ranks rather than the two names that used to exist. */
          const myRank = roleOf(crew.myRole ?? '')?.rank ?? (crew.myRole === 'owner' ? 0 : 99);
          const theirRank = roleOf(m.role)?.rank ?? 99;
          const mayTouch = can && !isMe && m.role !== 'owner' && (crew.myRole === 'owner' || theirRank > myRank);
          return (
            <li key={m.user.id} className="flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3">
              <Avatar user={m.user} size={36} presence />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-bold text-ink">{m.user.displayName}</span>
                <span className="block truncate text-[12px] text-mute">@{m.user.username}</span>
              </span>
              {m.role === 'owner' ? (
                <span className="flex flex-none items-center gap-1.5 rounded-full bg-[color-mix(in_srgb,var(--warn)_18%,transparent)] px-2.5 py-1 text-[11.5px] font-bold text-warn">
                  <Icon name="crown" size={12} />
                  Owner
                </span>
              ) : mayTouch ? (
                <span className="w-[150px] flex-none">
                  <Select
                    value={m.role}
                    disabled={busy}
                    onChange={(v) => void act('/api/crews/' + crew.id + '/members/' + m.user.id, { role: v }, 'Role changed')}
                    options={roles.map((r) => ({ value: r.id, label: r.name, sub: (counts.get(r.id) ?? 0) + ' in this role' }))}
                  />
                </span>
              ) : (
                <span
                  className="flex-none rounded-full px-2.5 py-1 text-[11.5px] font-bold"
                  style={{
                    color: roleOf(m.role)?.colour ?? 'var(--text-mute)',
                    background: 'color-mix(in srgb, ' + (roleOf(m.role)?.colour ?? 'var(--bg-3)') + ' 16%, transparent)'
                  }}
                >
                  {roleOf(m.role)?.name ?? m.role}
                </span>
              )}
              {mayTouch ? (
                <>
                  <Button
                    size="sm"
                    kind="ghost"
                    disabled={busy}
                    onClick={async () => {
                      const ok = await confirm('Remove ' + m.user.displayName + '?', 'They lose the tag and would have to be let back in.', { confirmLabel: 'Remove', danger: true });
                      if (ok) void act('/api/crews/' + crew.id + '/members/' + m.user.id, { remove: true }, 'Removed');
                    }}
                  >
                    Remove
                  </Button>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------- roles */

/* ------------------------------------------------------------ progress */

function Progress({ crew, tags, preview }: { crew: Crew; tags: Catalogue; preview: CrewChip }) {
  const unlocked = new Set<string>(crew.unlockedTags ?? []);
  const level = crew.level;
  const byLevel = new Map<number, CrewDesign[]>();
  for (const d of tags.designs) byLevel.set(d.level, [...(byLevel.get(d.level) ?? []), d]);

  return (
    <div className="flex max-w-[860px] flex-col gap-5">
      {/*
        One calculation, named.

        The crew's level comes from the combined levels of its members - the
        same number the crew page shows - and `into`/`need` are the distance
        through the current step. Two different numbers describing progress
        is how you end up with "7 of 10" beside "12 member levels", so both
        are labelled here and both come from the same object.
      */}
      <section className="rounded-[calc(16px*var(--radius-mul,1))] bg-surface-2 p-4">
        <div className="flex items-baseline gap-3">
          <h4 className="m-0 text-[17px] font-bold text-ink">Crew level {level.level}</h4>
          <span className="flex-1" />
          <span className="text-[13px] text-mute tabular-nums">
            {level.into} / {level.need} towards level {level.level + 1}
          </span>
        </div>
        <span className="mt-2.5 block h-2 overflow-hidden rounded-full bg-surface-4">
          <span
            className="block h-full rounded-full bg-[linear-gradient(90deg,var(--accent),var(--accent-hi))]"
            style={{ width: (level.need ? Math.round((level.into / level.need) * 100) : 0) + '%' }}
          />
        </span>
        <p className="mt-2 mb-0 text-[12.5px] leading-relaxed text-mute">
          Counted from the levels of everybody in the crew - {level.total} member levels in total.
          There is nothing to farm here that is not also somebody playing.
        </p>
      </section>

      {[...byLevel.entries()].sort((a, b) => a[0] - b[0]).map(([lv, list]) => {
        const open = lv <= level.level;
        return (
          <section key={lv} className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-4">
            <h4 className="m-0 mb-3 flex items-center gap-2 text-[14px] font-bold text-dim">
              <Icon name={open ? 'checkCirc' : 'lock'} size={15} className={open ? 'text-good' : 'text-ghost'} />
              {lv === 1 ? 'From the start' : 'Crew level ' + lv}
              {!open ? <span className="text-[12px] font-semibold text-ghost">· {lv - level.level} to go</span> : null}
            </h4>
            <div className="flex flex-wrap gap-3">
              {list.map((d) => (
                <span key={d.id} className={cn('flex flex-col items-center gap-1.5', !unlocked.has(d.id) && 'opacity-55')}>
                  <CrewTag crew={{ ...preview, design: d }} size="lg" still />
                  <span className="text-[11.5px] text-mute">{d.name}</span>
                </span>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------- notifications */

function Notifications({ crew }: { crew: Crew }) {
  const settings = useSession((s) => s.settings);
  const toast = useToast();
  const muted = (settings?.mutedCrews ?? []).includes(crew.id);
  const [busy, setBusy] = useState(false);

  async function setMuted(next: boolean) {
    setBusy(true);
    try {
      const list = new Set(settings?.mutedCrews ?? []);
      if (next) list.add(crew.id); else list.delete(crew.id);
      await api.patch('/api/me/settings', { mutedCrews: [...list] });
      await useSession.getState().refresh();
      toast(next ? 'Desktop alerts muted for this crew' : 'Desktop alerts back on', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[680px] flex-col gap-4">
      <Toggle
        checked={!muted}
        disabled={busy}
        onChange={(on: boolean) => void setMuted(!on)}
        label="Desktop alerts for this crew"
        note="Off silences the Windows notification and its sound for crew chat. The unread count still rises, and voice is a different system."
      />
      <p className="m-0 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3 text-[12.5px] leading-relaxed text-mute">
        This is your own preference and nobody else's. Crew-wide notification defaults - one setting the
        owner applies to everybody - are not something the server supports yet, so there is no switch for
        them here rather than one that quietly does nothing.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- danger */

function Danger({ crew, owner, onClose, onChanged }: {
  crew: Crew;
  owner: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [busy, setBusy] = useState(false);
  const others = crew.members.filter((m) => m.role !== 'owner');

  async function run(fn: () => Promise<unknown>, note: string, leave?: boolean) {
    setBusy(true);
    try {
      await fn();
      onChanged();
      toast(note, 'good');
      if (leave) { onClose(); go('crews'); }
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[680px] flex-col gap-5">
      {dialog}

      {owner ? (
        <Group title="Hand the crew over" note="They become the owner and you become an officer.">
          {others.length ? (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {others.map((m) => (
                <li key={m.user.id} className="flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3">
                  <Avatar user={m.user} size={32} />
                  <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold text-ink">{m.user.displayName}</span>
                  <Button
                    size="sm"
                    kind="outline"
                    disabled={busy}
                    onClick={async () => {
                      const ok = await confirm('Hand ' + crew.name + ' to ' + m.user.displayName + '?', 'You cannot take it back yourself.', { confirmLabel: 'Hand it over', danger: true });
                      if (ok) void run(() => api.post('/api/crews/' + crew.id + '/transfer', { userId: m.user.id }), 'They own it now');
                    }}
                  >
                    Make owner
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-[13.5px] text-mute">There is nobody else in the crew to hand it to.</p>
          )}
        </Group>
      ) : null}

      <Group title="Leave" note={owner ? 'Make somebody else the owner first, or the crew closes with you.' : 'You lose the tag. You would have to be let back in.'}>
        <Button
          kind="outline"
          disabled={busy}
          onClick={async () => {
            const ok = await confirm('Leave ' + crew.name + '?', owner
              ? 'You are the owner. Hand it over first, or the crew may close.'
              : 'Your tag goes with it.', { confirmLabel: 'Leave crew', danger: true });
            if (ok) void run(() => api.post('/api/crews/' + crew.id + '/leave', {}), 'You left ' + crew.name, true);
          }}
        >
          Leave crew
        </Button>
      </Group>

      {owner ? (
        <section className="rounded-[calc(14px*var(--radius-mul,1))] p-4 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_30%,transparent)]">
          <h4 className="m-0 mb-1.5 text-[15px] font-bold text-bad">Close the crew</h4>
          <p className="mt-0 mb-3 text-[13px] leading-relaxed text-mute">
            Everybody loses the tag, the chat goes, and the tag itself becomes free for somebody else to
            take. There is no undo.
          </p>
          <Button
            kind="outline"
            disabled={busy}
            onClick={async () => {
              const ok = await confirm('Close ' + crew.name + ' for good?', 'Every member loses the tag and the crew chat goes with it. This cannot be undone.', { confirmLabel: 'Close the crew', danger: true });
              if (ok) void run(() => api.del('/api/crews/' + crew.id), 'The crew is closed', true);
            }}
          >
            Close the crew
          </Button>
        </section>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------- pieces */

/**
 * A panel with a heading, the shape every section is built from.
 *
 * Same silhouette as the app's own settings cards so the crew screen does
 * not look like a different product wearing the same colours.
 */
function Card({ title, note, children, className }: {
  title: React.ReactNode;
  note?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("pugo-panel rounded-[calc(16px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]", className)}>
      <h4 className="m-0 text-[15.5px] font-bold text-ink">{title}</h4>
      {note ? <p className="mt-0.5 mb-3 text-[12.5px] leading-snug text-mute">{note}</p> : <div className="mb-3" />}
      {children}
    </section>
  );
}

function Group({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="m-0 text-[15.5px] font-bold text-ink">{title}</h4>
      {note ? <p className="mt-0.5 mb-3 text-[12.5px] text-mute">{note}</p> : <div className="mb-3" />}
      {children}
    </section>
  );
}

function Pick({ on, disabled, label, onClick, children }: {
  on: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-[72px] flex-col items-center justify-center gap-1.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-2 outline-none transition-[background-color,box-shadow]',
        on ? 'shadow-[inset_0_0_0_2px_var(--accent)]' : 'shadow-[inset_0_0_0_1px_var(--line-soft)]',
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-surface-3'
      )}
    >
      {children}
      <span className="text-[11.5px] font-semibold text-dim">{label}</span>
    </button>
  );
}

/** Says why a section is read-only, rather than leaving dead controls unexplained. */
function Locked({ can, why }: { can: boolean; why: string }) {
  if (can) return null;
  return (
    <p className="m-0 flex items-start gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3 text-[13px] leading-snug text-mute">
      <Icon name="lock" size={15} className="mt-px flex-none text-ghost" />
      {why}
    </p>
  );
}

/** The three starters, for a server that has not sent the catalogue. */
const FALLBACK: Catalogue = {
  designs: [{
    id: 'co-op', name: 'Co-op', level: 1, url: '/tags/co-op.svg',
    ink: '#e6f3e2', swatch: '#203d34', aspect: 240 / 72,
    textBox: { left: 76 / 240, right: 14 / 240, top: 12 / 72, bottom: 12 / 72 }
  }],
  inks: [{ id: 'frame', name: 'From the frame', colour: null }],
  fonts: [{ id: 'mono', name: 'Mono' }],
  textMax: 8,
  milestones: [1]
};
